/**
 * 基于 OSS 的去重文件存储服务
 *
 * 不以用户做区分，以MD5、文件大小、后缀名作为唯一标识，索引信息存于数据库中
 * 需要先向服务器 check 文件是否存在，如果存在则直接返回文件信息，否则由客户端上传到OSS
 * 文件存于 OSS，目录结构如下：
 * - dedup/ :
 *   - $md5_$size.$ext : 以md5、文件大小、后缀名命名的具体文件
 *   - $md5_$size.$ext/xxx : 文件进行二次处理（如pdf转图片）所产生的文件
 */

import axios, { type AxiosProgressEvent } from 'axios'

import { getFileMd5 } from '../file'
import { check, type DedupAssetFileInfo } from '../../api/dedup-asset'

/**
 * 上传文件到OSS
 * @param file - 要上传的文件对象
 * @param onUploadProgress - 上传进度回调函数
 * @returns Promise<DedupAssetFileInfo> - 返回文件信息
 */
export async function uploadDedupAsset(
  file: File,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
): Promise<DedupAssetFileInfo> {
  // 获取文件基本信息
  const fileName = file.name
  const size = file.size
  const md5 = await getFileMd5(file)

  // 检查文件是否已存在于OSS中
  const checkResponse = await check({ fileName, md5, size })
  if (checkResponse.code === 0) {
    const checkResult = checkResponse.data
    if (checkResult.exists) {
      // 如果文件已存在，直接返回文件信息
      return checkResult.fileInfo!
    } else {
      // 文件不存在，需要上传到OSS
      const postInfo = checkResult.postInfo!
      const bucketHost = postInfo.bucketHost
      // 构建上传参数
      const params = {
        ...postInfo.params,
        file,
      }
      // 执行文件上传
      const ossResponse = await axios.postForm(bucketHost, params, {
        onUploadProgress,
      })
      if (ossResponse.status === 200) {
        // 上传成功后再次检查文件状态
        const checkAgainResponse = await check({ fileName, md5, size })
        if (checkAgainResponse.code === 0) {
          return checkAgainResponse.data.fileInfo!
        } else {
          throw Error(checkAgainResponse.message)
        }
      } else {
        throw Error(ossResponse.statusText)
      }
    }
  } else {
    throw Error(checkResponse.message)
  }
}
